import { Currency, getLocale, Locale, LocaleShorthandUpper } from '@libs/core';

export type OfferMerchantListingSchema = {
    shippingDetails: {
        '@type': 'OfferShippingDetails';
        shippingDestination: { '@type': 'DefinedRegion'; addressCountry: string };
        shippingRate: { '@type': 'MonetaryAmount'; value: string; currency: string };
        deliveryTime: {
            '@type': 'ShippingDeliveryTime';
            handlingTime: { '@type': 'QuantitativeValue'; minValue: number; maxValue: number; unitCode: 'DAY' };
            transitTime: { '@type': 'QuantitativeValue'; minValue: number; maxValue: number; unitCode: 'DAY' };
        };
    };
    hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy';
        applicableCountry: string;
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow';
        merchantReturnDays: number;
        returnMethod: ('https://schema.org/ReturnInStore' | 'https://schema.org/ReturnByMail')[];
    };
};

const RETURN_POLICY_CATEGORY = 'https://schema.org/MerchantReturnFiniteReturnWindow';
const RETURN_METHODS = ['https://schema.org/ReturnInStore', 'https://schema.org/ReturnByMail'] as const;
const MERCHANT_RETURN_DAYS = 60;
const HANDLING_TIME = { minValue: 0, maxValue: 3, unitCode: 'DAY' as const };
const TRANSIT_TIME = { minValue: 1, maxValue: 3, unitCode: 'DAY' as const };

/** Standard shipping rates per locale — follow the published standard shipping policy. */
const STANDARD_SHIPPING_RATE: Record<Locale, string> = {
    [Locale['en-US']]: '6.99',
    [Locale['en-CA']]: '10.00',
};

const LOCALE_CURRENCY: Record<Locale, Currency> = {
    [Locale['en-US']]: Currency.USD,
    [Locale['en-CA']]: Currency.CAD,
};

/**
 * Site-wide Google merchant listing values per locale.
 * Rates, 0-3 day handling, and 1-3 day transit follow the published standard shipping policy.
 * Returns follow the 60-day receipt window, accepted in store and by mail.
 */
const buildMerchantListingSchemaForLocale = (locale: Locale): OfferMerchantListingSchema => {
    const country = LocaleShorthandUpper[locale];

    return {
        shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: { '@type': 'DefinedRegion', addressCountry: country },
            shippingRate: {
                '@type': 'MonetaryAmount',
                value: STANDARD_SHIPPING_RATE[locale],
                currency: LOCALE_CURRENCY[locale],
            },
            deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: { '@type': 'QuantitativeValue', ...HANDLING_TIME },
                transitTime: { '@type': 'QuantitativeValue', ...TRANSIT_TIME },
            },
        },
        hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: country,
            returnPolicyCategory: RETURN_POLICY_CATEGORY,
            merchantReturnDays: MERCHANT_RETURN_DAYS,
            returnMethod: [...RETURN_METHODS],
        },
    };
};

const MERCHANT_LISTING_SCHEMA: Record<Locale, OfferMerchantListingSchema> = {
    [Locale['en-US']]: buildMerchantListingSchemaForLocale(Locale['en-US']),
    [Locale['en-CA']]: buildMerchantListingSchemaForLocale(Locale['en-CA']),
};

export const buildOfferMerchantListingSchema = (): OfferMerchantListingSchema => MERCHANT_LISTING_SCHEMA[getLocale()];
